import React, { createContext, useCallback, useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  FluxoDeCaixaContextData,
  FluxoDeCaixaProviderProps,
  FormType,
} from '../types/context';
import {
  ResponseProps,
  rowsProps,
  Response,
  totalizadoresProps,
  ILoja,
  periodoProps,
  chartProps,
} from '../types';
import { yupResolver } from '@hookform/resolvers/yup';
import { schema } from '../validations';
import { LojaContext } from '~/context/loja';
import api from '~/services/api';
import { formatCurrencyAsText } from '~/utils/functions';
import { toast } from 'react-toastify';

export const FluxoDeCaixaContext = createContext({} as FluxoDeCaixaContextData);

const totalizadoresDefault = {
  val_inicial: 0,
  val_final: 0,
  val_total_receber: 0,
  val_total_pagar: 0,
  val_total_consolidado: 0,
};

export const FluxoDeCaixaProvider: React.FC<FluxoDeCaixaProviderProps> = ({
  children,
}) => {
  const { loja } = useContext(LojaContext);
  const [lojas, setLojas] = useState<number | number[] | undefined>(undefined);
  const [tab, setTab] = useState<string>('grid');
  const [rows, setRows] = useState<rowsProps[]>([]);
  const [chartInfos, setChartInfos] = useState<chartProps[]>([]);
  const [cleanCC, setCleanCC] = useState<boolean>(false);
  const [disabledFields, setDisabledFields] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [infoLoja, setInfoLoja] = useState<ILoja | null>(null);
  const [periodo, setPeriodo] = useState<periodoProps>({
    dta_inicio: '',
    dta_final: '',
  });
  const [totalizadores, seTotalizadores] =
    useState<totalizadoresProps>(totalizadoresDefault);
  const {
    register,
    getValues,
    handleSubmit,
    formState,
    control,
    setValue,
    watch,
    reset,
    setFocus,
    clearErrors,
    setError,
    resetField,
  } = useForm({
    resolver: yupResolver(schema),
    reValidateMode: 'onChange',
  });

  const formTela: FormType = {
    register,
    getValues,
    handleSubmit,
    formState,
    control,
    setValue,
    watch,
    reset,
    setFocus,
    clearErrors,
    setError,
    resetField,
  };

  const handleChangeLojas = useCallback((value: number | number[]) => {
    setLojas(value);
  }, []);

  const onSearch = handleSubmit(async () => {
    try {
      setLoading(true);
      const { dta_inicio, dta_final, cod_cc } = getValues();
      if (!cod_cc || cod_cc.length <= 0) {
        return toast.warn('Ao menos uma conta deve ser selecionada');
      }
      const { data } = await api.get<ResponseProps>('/fluxo-de-caixa', {
        params: {
          dta_inicio,
          dta_final,
          lojas,
          cod_cc,
        },
      });
      if (!data.success) return;
      const gradeFormatted = data.data.grade.map((item: Response) => {
        return {
          ...item,
          val_total_receber: formatCurrencyAsText(item.val_total_receber),
          val_total_pagar: formatCurrencyAsText(item.val_total_pagar),
          val_total_consolidado: formatCurrencyAsText(
            item.val_total_consolidado,
          ),
          val_saldo: formatCurrencyAsText(item.val_saldo),
        };
      });

      const [yearIni, monthIni, dayIni] = dta_inicio.split('-');
      const [yearFim, monthFim, dayFim] = dta_final.split('-');
      const formattedInicio = `${dayIni}/${monthIni}/${yearIni}`;
      const formattedFinal = `${dayFim}/${monthFim}/${yearFim}`;
      setPeriodo({
        dta_inicio: formattedInicio,
        dta_final: formattedFinal,
      });
      setRows(gradeFormatted);
      setChartInfos(data.data.grafico);
      seTotalizadores(data.data.totalizadores);
      setInfoLoja(loja);
      setTab('grid');
      setDisabledFields(true);
    } finally {
      setLoading(false);
    }
  });

  const onCancel = () => {
    setDisabledFields(false);
  };

  const onClear = useCallback(() => {
    setCleanCC(true);
    reset({
      dta_inicio: '',
      dta_final: '',
    });
    setRows([]);
    setChartInfos([]);
    seTotalizadores(totalizadoresDefault);
    setInfoLoja(null);
    setTab('grid');
  }, [reset]);

  return (
    <FluxoDeCaixaContext.Provider
      value={{
        formTela,
        tab,
        setTab,
        handleChangeLojas,
        lojas,
        onSearch,
        onCancel,
        onClear,
        rows,
        setRows,
        cleanCC,
        setCleanCC,
        disabledFields,
        loading,
        chartInfos,
        infoLoja,
        totalizadores,
        periodo,
      }}
    >
      {children}
    </FluxoDeCaixaContext.Provider>
  );
};
